import { FORMAT_DATE } from 'common/constants';
import { REQUEST_TYPE, TYPE_OF_WORK } from 'common/constants/insulation';
import { useAuth } from 'features/auth';
import { NodeItem, RequestDataType, Status } from 'features/insulation';
import { correctData, getRequestVisibile } from 'features/insulation/utils';
import {
  flattenDeep,
  forEach,
  get,
  isArray,
  isEmpty,
  isNull,
  isObject,
  isUndefined,
  map,
  memoize,
  uniq,
} from 'lodash';
import moment from 'moment';
import { StatusInstance } from './hooks/useStatus';

const isValidValue = (value: any) => {
  let isValid = true;
  if (typeof value === 'string' && value.trim().length === 0) {
    isValid = false;
  }
  if (isObject(value) && isEmpty(value)) {
    isValid = false;
  }
  if (isUndefined(value) || isNull(value)) {
    isValid = false;
  }
  return isValid;
};

export const checkValidateData = (
  checkedData: any,
  modelForm: any,
): string[] => {
  const arrayValidate: any = {};
  const data = correctData(checkedData);
  forEach(modelForm, function (values, key) {
    let unfilled = 0;
    map(values, function (value) {
      if (!isValidValue(get(data, value))) {
        unfilled++;
      }
    });
    if (unfilled === values.length) {
      arrayValidate[key] = 'none';
    } else if (unfilled === 0) {
      arrayValidate[key] = 'done';
    } else {
      arrayValidate[key] = 'none';
    }
  });
  return arrayValidate;
};

export const checkValidateToSubmit = (
  checkedData: any,
  panels: RequestDataType[number],
  helpers?: any,
): string[] => {
  const arrayValidate: string[] = [];
  const data = correctData(checkedData);
  forEach(panels, function (panel) {
    forEach(panel?.validate, function (value) {
      let checkKey;
      if (value instanceof Function) {
        checkKey = value(data, helpers);
        if (isArray(checkKey)) {
          forEach(checkKey, function (key) {
            if (key && !isValidValue(get(data, key))) {
              arrayValidate.push(panel.key);
            }
          });
          return;
        }
      } else {
        checkKey = value;
      }
      if (checkKey && !isValidValue(get(data, checkKey))) {
        arrayValidate.push(panel.key);
      }
    });
  });
  return uniq(arrayValidate);
};

export const getTypeOfWorkByURL = (type: any) => {
  let typeOfWork = '';
  TYPE_OF_WORK.forEach(function (item) {
    if (item.link === get(REQUEST_TYPE, type) && item.label) {
      typeOfWork = item.label;
    } else if (item.link === get(REQUEST_TYPE, type)) {
      typeOfWork = item.tilte;
    }
  });
  return typeOfWork ? typeOfWork : '';
};

export const getServiceByURL = (type: any) => {
  let typeOfWork = 1;
  TYPE_OF_WORK.forEach(function (item) {
    if (item.link === get(REQUEST_TYPE, type) && item.label) {
      typeOfWork = item.id;
    }
  });
  return typeOfWork ? typeOfWork : 1;
};

export const convertMomentToString = (data: any) => {
  const newData = { ...data };
  forEach(data, function (value, key) {
    if (moment.isMoment(value)) {
      newData[key] = moment(value).format(FORMAT_DATE.FULL_DATETIME);
    }
  });
  return newData;
};

export const getPanelKeysValidate = (
  status: Status | undefined,
  instatementPanels: RequestDataType,
) => {
  const { Done, Pending } = getRequestVisibile(
    status,
    Object.keys(instatementPanels) as Status[],
  );
  let panelKeysDone: string[] = [];
  let panelKeysPending: string[] = [];
  forEach(Done, i => {
    if (instatementPanels[i]) {
      panelKeysDone = get(instatementPanels, i).map(u => u.key);
    }
  });
  forEach(Pending, i => {
    if (instatementPanels[i]) {
      panelKeysPending = get(instatementPanels, i).map(u => u.key);
    }
  });
  return panelKeysDone.concat(panelKeysPending);
};

export const isEdit: (
  roles: NodeItem['roles'],
  status: StatusInstance,
  auth: ReturnType<typeof useAuth>[0],
) => boolean = (roles, status, auth) => {
  if (!roles || !status?.status || status?.state === 'VIEW') {
    return false;
  }
  for (const role of roles) {
    if (role?.[0] === status?.status && auth.checkRole(...role)) {
      return true;
    }
  }
  return false;
};

export const validateData = (
  actionProps: any,
  panelConfig: RequestDataType,
  currentStatus?: string,
  helpers?: any,
) => {
  const result: { [name: Status[number]]: string[] } = {};
  Object.keys(panelConfig).forEach(key => {
    result[key] = [];
  });
  if (currentStatus) {
    return {
      ...result,
      [currentStatus]: checkValidateToSubmit(
        actionProps?.getFormValues(),
        panelConfig[currentStatus],
        helpers,
      ),
    };
  }
  return result;
};

export const getValueByStatus = (listRequestStatus: any, status: any) => {
  if (status?.includes('PENDING_PAINTING_')) {
    return get(listRequestStatus, 'PENDING_PAINTING')?.VALUE;
  } else if (status?.includes('PENDING_QAQC_')) {
    return get(listRequestStatus, 'PENDING_QAQC')?.VALUE;
  } else {
    return get(listRequestStatus, status || 'DRAFT')?.VALUE;
  }
};

export const getSuffixName = (stage: any, totalStage: any) => {
  if (stage < totalStage) {
    switch (stage) {
      case '1':
        return `1st`;
      case '2':
        return `2nd`;
      case '3':
        return `3rd `;
      case '4':
        return `4th`;
      default:
        return `1st `;
    }
  } else if (stage == totalStage) {
    return `Final`;
  }
};

export const getFieldsValidateReinstatements = (data: any) => {
  const proposalBillingDetail = isArray(data?.proposalBillingDetail?.data)
    ? data?.proposalBillingDetail?.data
    : [];
  const results: string[] = proposalBillingDetail?.map((u: any, i: any) => [
    `proposalBillingDetail.data[${i}].finalMeasurement`,
    `proposalBillingDetail.data[${i}].finalCost`,
  ]);
  return flattenDeep(results);
};
